/**
 * Which surfaces belong to a TENANT's audience rather than to a PanLuma user.
 *
 * PanLuma dogfoods its own support widget, and the widget key it loads is
 * PanLuma's own tenant's. That is right on the pages a PanLuma user works in and
 * wrong everywhere a tenant points somebody else: a customer of Meridian Global
 * Logistics reading Meridian's white-labelled portal has no relationship with
 * PanLuma, so "Report a bug or give feedback" hands them the wrong support desk
 * entirely — and the fixed launcher also sits over their content at 375px.
 *
 * The prefixes below are the public/portal routes `App.tsx` registers whose
 * reader is the tenant's counterparty: their customer, candidate, subscriber,
 * signer, payer or ticket requester. Deliberately NOT here: `/login`,
 * `/register`, `/join` and `/kiosk`, whose reader is a PanLuma user (a tenant's
 * own employee) and who is exactly who the widget is for; and `/status`, which
 * is PanLuma's own status page.
 *
 * If a tenant should one day get a widget of its own on its portal, that is a
 * different widget key resolved from the tenant — not this one.
 *
 * Pinned against the real route table by
 * `__tests__/supportWidgetSurfaces.test.ts`: a prefix matching no registered
 * route guards nothing and looks exactly like one that works.
 */
export const TENANT_AUDIENCE_PATH_PREFIXES = [
  /** White-labelled customer portal, `/external/:tenantSlug/*`. */
  '/external',
  /**
   * Legacy second mount of the same support-portal pages. Scoped to the
   * segment `App.tsx` actually registers rather than a bare `/portal`, so a
   * future PanLuma-user route elsewhere under `/portal` is not swallowed.
   */
  '/portal/support',
  /** Public career portal — candidates applying to the tenant. */
  '/careers',
  /** Public forms the tenant publishes. */
  '/forms',
  /**
   * Newsletter subscribe / confirm / unsubscribe. Spelled per public route and
   * NOT as a bare `/marketing`, which is the internal Marketing module's own
   * first segment — the same trap `/support` below is split for.
   */
  '/marketing/subscribe',
  '/marketing/confirm',
  '/marketing/unsubscribe',
  /** Token-protected contract signing. */
  '/sign',
  /** Token-protected invoice payment. */
  '/pay',
  /** Token-protected self-service installment payment. */
  '/pay-installment',
  /** Token-protected public document view. */
  '/document',
  /** Token-protected freight-quote client approval. */
  '/freight-quote-approval',
  /** Public CSAT landing for a ticket the tenant's customer filed. */
  '/support/csat',
  /** Public magic-link ticket view for the same reader. */
  '/support/ticket-link',
] as const;

/**
 * True when `pathname` is a page a tenant points its own audience at, so no
 * PanLuma-owned chrome may render on it.
 *
 * Matches on a full segment boundary: the agent-facing `/support` module keeps
 * the widget while `/support/csat/:ticketId` does not.
 */
export function isTenantAudienceSurface(pathname: string): boolean {
  return TENANT_AUDIENCE_PATH_PREFIXES.some(
    (prefix) => pathname === prefix || pathname.startsWith(`${prefix}/`),
  );
}
